// "Por hacer" de un presupuesto FUERA de la ficha (Mesa de control).
//
// Monta las mismas entradas que `calcularTareasFicha()` de
// pages/PresupuestoDetalle.js con la ficha recién abierta (formato nuevo) y
// llama a la misma función, `calcularTareasPorHacer` (utils/tareasPresupuesto.js):
// mismos GET, mismas transformaciones, mismo resultado que el contador de la
// pestaña "Por hacer". Solo lectura: no escribe nada.
//
// Si cambia calcularTareasFicha() en la ficha, hay que cambiar esto igual
// (cada clave lleva al lado de dónde sale en la ficha).
import { api, presupuestosAPI, menusAPI, platosAPI } from '../api/api';
import {
  calcularTareasPorHacer, obtenerFechasEvento, textoObservacionesPresupuesto,
  avisoRutaPresupuesto, avisoFinSinDefinir, jornadasParaTareas,
} from './tareasPresupuesto';
import { resumenAlquilerJornadas, eventoPasado } from './multieventoCabecera';
import { resumenAdaptaciones } from './multieventoPantallas';
import { personasFila } from './quienVaEvento';
import { calcularCosteOrdenPersonal, ordenDeJornada } from './ordenPersonalExtra';
import { ALERGENOS_OFICIALES, DIETAS, TEXTO_ESTADO_LISTA } from './alergenosPresupuesto';

// Igual que en la ficha
const formatEuros = (n) => new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(Number(n) || 0);
const formatFecha = (fechaStr) => {
  if (!fechaStr) return '';
  const fecha = new Date(fechaStr);
  const dia = String(fecha.getDate()).padStart(2, '0');
  const mes = String(fecha.getMonth() + 1).padStart(2, '0');
  return `${dia}/${mes}/${fecha.getFullYear()}`;
};

// Líneas automáticas que la ficha sustituye al llegar las reglas (efecto de
// reglas-comerciales): mínimo, Madrid Central y hora extra del lunch
const firmaLineaMinimo = (sups) => JSON.stringify(
  (sups || []).filter(s => s.es_suplemento_minimo || s.es_suplemento_madrid_central
    || s.es_suplemento_hora_extra_lunch)
    .map(s => [s.id, s.unidades, s.precio_unitario, s.total, !!s.es_cortesia])
);

// Catálogos de menús y platos: solo los necesita un presupuesto antiguo sin
// líneas propias (menus_ids) o con platos sueltos (platos_ids), para contar
// sus filas como hace initializeItems()
export const necesitaCatalogos = (p) => {
  const sinLineasPropias = !(Array.isArray(p?.menus_personalizados) && p.menus_personalizados.length > 0);
  return (sinLineasPropias && (p?.menus_ids || []).length > 0) || (p?.platos_ids || []).length > 0;
};

export const cargarCatalogosPorHacer = async () => {
  const [menus, platos] = await Promise.all([menusAPI.getAll(), platosAPI.getAll()]);
  return { menus: menus?.data || [], platos: platos?.data || [] };
};

// Filas de la ficha (`items.length` tras initializeItems)
const numFilasFicha = (p, catalogos) => {
  let n = 0;
  if (Array.isArray(p.menus_personalizados) && p.menus_personalizados.length > 0) {
    n += p.menus_personalizados.filter(Boolean).length;
  } else if ((p.menus_ids || []).length > 0) {
    const menus = catalogos?.menus || [];
    n += p.menus_ids.filter(mid => menus.some(m => m.id === mid)).length;
  }
  if ((p.platos_ids || []).length > 0) {
    const platos = catalogos?.platos || [];
    n += p.platos_ids.filter(pid => platos.some(pl => pl.id === pid)).length;
  }
  return n;
};

// GET de un presupuesto con lo mismo que pide la ficha al abrirse. Cada GET
// que falle se queda en null, como en la ficha.
export const cargarDatosPorHacer = async (id) => {
  const get = (url) => api.get(url).then(r => r.data).catch(() => null);
  const [presupuestoRes, reglas, envio, cobro, alquiler, material, bebidas] = await Promise.all([
    presupuestosAPI.getOne(id).then(r => r.data),
    get(`/presupuestos/${id}/reglas-comerciales`),
    get(`/presupuestos/${id}/estado-envio`),
    get(`/presupuestos/${id}/cobro-previo`),
    get(`/presupuestos/${id}/alquiler`),
    get(`/presupuestos/${id}/material-evento/estado`),
    get(`/presupuestos/${id}/bebidas-modo`),
  ]);
  return {
    presupuesto: presupuestoRes,
    // Mismas transformaciones que los setState de la ficha
    estadoReglas: reglas || null,
    estadoEnvio: envio || null,
    cobroPrevio: cobro && cobro.motivo ? cobro : null,
    estadoAlquiler: alquiler && Array.isArray(alquiler.lineas) ? alquiler : null,
    estadoMaterial: material && Array.isArray(material.ambitos) ? material : null,
    estadoBebidasModo: bebidas && bebidas.modo ? bebidas : null,
  };
};

// Tareas de un presupuesto con los datos de cargarDatosPorHacer()
export const tareasPorHacerDeDatos = (datos, { catalogos = null, hoy = new Date() } = {}) => {
  const {
    estadoReglas, estadoEnvio, cobroPrevio, estadoAlquiler, estadoMaterial, estadoBebidasModo,
  } = datos;
  let presupuesto = datos.presupuesto;
  // Efecto de reglas de la ficha: si cambian las líneas automáticas, las de las reglas
  if (estadoReglas && firmaLineaMinimo(presupuesto.suplementos) !== firmaLineaMinimo(estadoReglas.suplementos)) {
    presupuesto = {
      ...presupuesto,
      suplementos: estadoReglas.suplementos || [],
      importe_base: estadoReglas.importe_base ?? presupuesto.importe_base,
      iva: estadoReglas.iva ?? presupuesto.iva,
      importe_total: estadoReglas.importe_total ?? presupuesto.importe_total,
    };
  }

  const { inicio, fin } = obtenerFechasEvento(presupuesto);
  const jornadasP = presupuesto.jornadas || [];

  // Totales (basePresupuesto / totalPresupuesto): los del servidor
  const esMultiConJornadas = !!(presupuesto.es_multi_evento && jornadasP.length > 0);
  const total = Number(presupuesto.importe_total) || 0;
  let base;
  if (esMultiConJornadas) {
    const ivaN = Number(presupuesto.iva);
    const iva = presupuesto.iva !== null && presupuesto.iva !== undefined && Number.isFinite(ivaN)
      ? ivaN
      : (Number(presupuesto.importe_base) > 0 ? total - Number(presupuesto.importe_base) : 0);
    base = total - iva;
  } else {
    base = Number(presupuesto.importe_base) || 0;
  }

  // calcularCicloEvento(): facturado y facturación por jornada (con la ficha
  // recién abierta no hay facturas cargadas: cuenta la marca de cada jornada)
  const porJornadaFact = !!(presupuesto.es_multi_evento && presupuesto.facturacion_multi_evento === 'por_jornada'
    && jornadasP.length > 0);
  const jornadasConId = jornadasP.filter(j => j && j.id);
  const facturacionJornadas = porJornadaFact
    ? { emitidas: jornadasConId.filter(j => !!(j.numero_factura || j.facturada)).length, total: jornadasConId.length, lista: null }
    : null;
  const facturado = facturacionJornadas
    ? facturacionJornadas.total > 0 && facturacionJornadas.emitidas === facturacionJornadas.total
    : !!(presupuesto.tiene_factura || presupuesto.numero_factura
      || (presupuesto.es_multi_evento && jornadasP.length > 0 && jornadasP.every(j => j.numero_factura || j.facturada)));

  // Cóctel
  let coctel = null;
  if (estadoReglas) {
    coctel = [];
    const anadir = (c, etiqueta) => coctel.push({
      etiqueta,
      personas: c.personas,
      recomendados: c.camareros_recomendados,
      asignados: c.camareros_asignados,
      faltan: c.faltan_camareros,
    });
    if (estadoReglas.coctel?.aplica) anadir(estadoReglas.coctel, null);
    (estadoReglas.jornadas || []).forEach((j) => {
      if (j.coctel?.aplica) anadir(j.coctel, `Jornada ${j.indice}${j.fecha ? ` (${formatFecha(j.fecha).slice(0, 5)})` : ''}`);
    });
  }

  // getResumenAlergenos()
  const resumenAlergenos = (() => {
    const comensales = (presupuesto.comensales_alergicos || []).filter(Boolean);
    const lista = presupuesto.alergenos_lista || {};
    const estado = lista.estado || 'sin_lista';
    const porAlergeno = new Map();
    const porDieta = new Map();
    const personas = (c) => Math.max(1, parseInt(c.cantidad, 10) || 1);
    comensales.forEach((c) => (c.alergenos_ids || []).forEach((aid) => porAlergeno.set(aid, (porAlergeno.get(aid) || 0) + personas(c))));
    comensales.forEach((c) => (c.dietas || []).forEach((did) => porDieta.set(did, (porDieta.get(did) || 0) + personas(c))));
    const texto = [
      ...[...porAlergeno.entries()]
        .map(([aid, n]) => `${n} ${(ALERGENOS_OFICIALES.find(a => a.id === aid)?.nombre || aid).toLowerCase()}`),
      ...[...porDieta.entries()]
        .map(([did, n]) => `${n} ${(DIETAS.find(dd => dd.id === did)?.nombre || did).toLowerCase()}`),
    ].join(' · ');
    return {
      comensales: comensales.reduce((s, c) => s + personas(c), 0),
      estado,
      estadoTexto: TEXTO_ESTADO_LISTA[estado] || estado,
      texto,
      huellaCambiada: !!(lista.huella_cambiada && estado === 'validada_cocina'),
      aviso: lista.aviso || null,
      sinAlergicosPor: lista.sin_alergicos_por || null,
      sinAlergicosEn: lista.sin_alergicos_en || null,
    };
  })();

  // getQuienVa() (de la orden de personal extra solo cuentan hayOrden y personasOrden)
  const quienVa = (() => {
    const q = presupuesto.quien_va_evento || {};
    const modo = q.modo || null;
    const sumaPersonas = (filas) => (filas || []).reduce((s, f) => s + personasFila(f), 0);
    const jornadasQv = estadoReglas?.quien_va?.jornadas || [];
    const filasMenu = estadoReglas?.quien_va?.personal_menu || q.personal_menu || [];
    const personasMenu = jornadasQv.length > 0
      ? Math.max(0, ...jornadasQv.map(j => sumaPersonas(j.personal_menu)))
      : sumaPersonas(filasMenu);
    if (!modo && personasMenu === 0) return null;
    let hayOrden = false;
    let personasOrden = 0;
    if (esMultiConJornadas) {
      jornadasP.forEach((j) => {
        const c = calcularCosteOrdenPersonal(ordenDeJornada(presupuesto, j));
        if (c.total_personas <= 0) return;
        hayOrden = true;
        personasOrden += c.total_personas;
      });
    } else {
      const c = calcularCosteOrdenPersonal(presupuesto.orden_personal_extra);
      if (c.total_personas > 0) {
        hayOrden = true;
        personasOrden = c.total_personas;
      }
    }
    return {
      modo,
      personasMenu,
      personasExternas: personasMenu + (modo === 'extra' ? 1 : 0),
      hayOrdenExtra: hayOrden,
      personasOrden,
    };
  })();

  return calcularTareasPorHacer({
    hoy,
    compraResumen: presupuesto.estado === 'confirmado' ? (presupuesto.compra_resumen ?? null) : undefined,
    ahora: new Date(),
    estado: presupuesto.estado,
    fechaInicio: inicio,
    fechaFin: fin,
    // Recién abierta, la ficha no tiene el aviso de un guardado
    confirmadoModificado: presupuesto.confirmado_modificado || null,
    numMenus: numFilasFicha(presupuesto, catalogos),
    numSuplementos: (presupuesto.suplementos || []).length,
    baseImponible: base,
    totalConIvaTexto: formatEuros(total),
    envios: (presupuesto.registros_envios || []).filter(e => (e?.tipo_documento || 'presupuesto') === 'presupuesto'),
    bebidasConfirmadas: !!presupuesto.bebidas_confirmadas,
    bebidasModo: estadoBebidasModo?.modo || null,
    bebidasModoValidadoEn: estadoBebidasModo?.validado_en || null,
    bebidasModoAplica: estadoBebidasModo ? estadoBebidasModo.aplica !== false : null,
    hayBebidasPersonalizadas: (presupuesto.bebidas || []).length > 0,
    coctel,
    adaptaciones: resumenAdaptaciones(presupuesto),
    alergenos: resumenAlergenos,
    observacionesTexto: textoObservacionesPresupuesto(presupuesto),
    numPersonal: (() => {
      const lista = presupuesto.personal_asignado || [];
      const pid = presupuesto.quien_va_evento?.persona_id;
      return lista.length + (pid && !lista.some(p => p.empleado_id === pid) ? 1 : 0);
    })(),
    numFurgonetas: (presupuesto.vehiculos_asignados || []).length,
    lugarEvento: presupuesto.lugar_evento,
    distanciaKm: presupuesto.distancia_km,
    avisoRuta: avisoRutaPresupuesto(presupuesto),
    avisoHoraFin: avisoFinSinDefinir(presupuesto),
    // El bloque de sobrante está desactivado en calcularTareasPorHacer
    sobrante: null,
    contactoFacturacion: !!presupuesto.contacto_facturacion_id,
    facturado,
    facturacionJornadas: facturacionJornadas
      ? { emitidas: facturacionJornadas.emitidas, total: facturacionJornadas.total }
      : null,
    numeroFactura: presupuesto.numero_factura || null,
    estadoPago: presupuesto.estado_pago,
    envio: estadoEnvio && estadoEnvio.modificado_tras_envio ? {
      modificado: true,
      cambios: estadoEnvio.cambios || [],
      fecha: estadoEnvio.ultimo_envio?.fecha || null,
    } : null,
    cobroPrevio,
    quienVa,
    alquilerJornadas: resumenAlquilerJornadas(presupuesto),
    alquiler: estadoAlquiler && ((estadoAlquiler.lineas || []).length > 0 || estadoAlquiler.hay_recepcion) ? {
      lineas: estadoAlquiler.lineas.length,
      validado: !!estadoAlquiler.validado,
      validadoPor: estadoAlquiler.validado?.por || null,
      huellaCambiada: !!estadoAlquiler.huella_cambiada,
      estadoOrden: estadoAlquiler.orden ? (estadoAlquiler.orden.estado || 'borrador') : null,
      pagadoEn: estadoAlquiler.orden?.fianza_pagada_en || null,
      fianzaDevueltaEn: estadoAlquiler.orden?.fianza_devuelta_en || null,
      proveedor: estadoAlquiler.orden?.proveedor_nombre || estadoAlquiler.proveedor?.nombre || null,
      avisoFechas: estadoAlquiler.aviso_fechas || null,
      cotizacion: estadoAlquiler.orden?.cotizacion_recibida || null,
      hayRecepcion: !!estadoAlquiler.hay_recepcion,
      recepcionAplica: !!estadoAlquiler.recepcion_aplica,
      recepcionComprobada: !!estadoAlquiler.recepcion?.comprobada,
      recepcionPor: estadoAlquiler.recepcion?.por || null,
      recepcionEn: estadoAlquiler.recepcion?.en || null,
      recepcionIncidencias: estadoAlquiler.recepcion?.incidencias || '',
    } : null,
    material: estadoMaterial ? {
      validado: !!estadoMaterial.validado,
      validados: estadoMaterial.validados || 0,
      total: (estadoMaterial.ambitos || []).length,
      huellaCambiada: !!estadoMaterial.huella_cambiada,
      por: (estadoMaterial.ambitos || [])[0]?.por || null,
    } : null,
    suplementoMinimo: estadoReglas?.suplemento_minimo ? {
      minimo: estadoReglas.suplemento_minimo.minimo,
      pendientes: (estadoReglas.suplemento_minimo.pendientes || []).map(sp => ({
        clave: sp.clave,
        servicio: sp.menu_vinculado_nombre || sp.formato_nombre || 'el servicio',
        jornada: sp.jornada_etiqueta || null,
        faltan: sp.faltan,
        importeTexto: `${sp.faltan} × ${formatEuros(sp.tarifa)} = ${formatEuros(sp.importe)}`,
      })),
    } : null,
    horaExtraLunch: estadoReglas?.hora_extra_lunch ? {
      pendientes: (estadoReglas.hora_extra_lunch.pendientes || []).map(sp => ({
        clave: sp.clave,
        jornada: sp.jornada_etiqueta || null,
        horaFin: sp.hora_fin,
        minutosExtra: sp.minutos_extra,
        importeTexto: formatEuros(sp.importe),
      })),
    } : null,
    horarios: estadoReglas?.horarios ? {
      aplica: !!estadoReglas.horarios.aplica,
      servicios: estadoReglas.horarios.servicios || 0,
      sinHora: estadoReglas.horarios.sin_hora || 0,
      nombresSinHora: estadoReglas.horarios.nombres_sin_hora || [],
      solapes: (estadoReglas.horarios.solapes_pendientes || [])
        .map(sol => ({ clave: sol.clave_decision, texto: sol.texto })),
    } : null,
    jornadas: jornadasParaTareas(presupuesto, {
      materialAmbitos: estadoMaterial?.ambitos,
      facturasJornada: null,
    }),
    modoFacturacion: presupuesto.facturacion_multi_evento || null,
  });
};

// Celebrado con el criterio de la ficha (reexportado para la Mesa de control)
export { eventoPasado };
